import { ColorPreset } from './types';

export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

export const exportPresetAsJSON = (preset: ColorPreset) => {
  const dataStr = JSON.stringify(preset, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${preset.name.replace(/\s+/g, '-').toLowerCase()}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Delay revoking the URL to ensure download starts
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
};

export const exportPresetAsCSS = (preset: ColorPreset) => {
  let cssVariables = '';
  
  preset.colors.forEach(color => {
    // Get the current format for this color (default to 'hsl' if not set)
    const colorFormat = color.format || 'hsl';
    const baseValue = getColorValue(color.hex, colorFormat);
    cssVariables += `  ${color.variable}: ${baseValue};\n`;
    
    // Shades
    if (color.shadesEnabled === true && color.shadesCount && color.shadesCount > 0) {
      const actualShadesCount = Math.min(color.shadesCount, 5); // Cap at 5
      const shades = color.customShades || generateShades(color.hex, actualShadesCount);
      shades.forEach(shade => {
        const shadeValue = getColorValue(shade.hex, colorFormat);
        cssVariables += `  ${color.variable}${shade.variable}: ${shadeValue};\n`;
      });
    }
    
    // Tints
    if (color.tintsEnabled === true && color.tintsCount && color.tintsCount > 0) {
      const actualTintsCount = Math.min(color.tintsCount, 5); // Cap at 5
      const tints = color.customTints || generateTints(color.hex, actualTintsCount);
      tints.forEach(tint => {
        const tintValue = getColorValue(tint.hex, colorFormat);
        cssVariables += `  ${color.variable}${tint.variable}: ${tintValue};\n`;
      });
    }
    
    // Transparencies
    if (color.transparencyEnabled === true && color.transparencyCount && color.transparencyCount > 0) {
      const transparencyFormat = color.transparencyFormat || 'hsla';
      const actualTransparencyCount = Math.min(color.transparencyCount, 10); // Cap at 10
      const transparencies = generateTransparencies(color.hex, actualTransparencyCount);
      transparencies.forEach(trans => {
        const transValue = getTransparencyColorValue(trans.hex, trans.opacity, transparencyFormat);
        cssVariables += `  ${color.variable}${trans.variable}: ${transValue};\n`;
      });
    }
  });
  
  const cssContent = `:root {\n${cssVariables}}`;
  
  const dataBlob = new Blob([cssContent], { type: 'text/css' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${preset.name.replace(/\s+/g, '-').toLowerCase()}.css`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Delay revoking the URL to ensure download starts
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
};

// Helper functions for color conversion (moved from ColorSwatch.tsx)
export const getColorValue = (hex: string, format: string) => {
  switch (format) {
    case 'rgb':
      return hexToRgb(hex);
    case 'hsl':
      return hexToHsl(hex);
    case 'oklch':
      return hexToOklch(hex);
    default:
      return hex;
  }
};

export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 'Invalid';
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgb(${r}, ${g}, ${b})`;
};

export const hexToHsl = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 'Invalid';
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
};

export const hexToHslValues = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

export const hexToOklch = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 'Invalid';
  
  // Normalize RGB to 0-1
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  // 1. Linearize RGB
  const linearize = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  r = linearize(r);
  g = linearize(g);
  b = linearize(b);

  // 2. Linear RGB to OKLab
  // Using simplified matrix for OKLab directly from Linear RGB
  const l_lab = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const a_lab = 0.2119034982 * r + 0.6740309940 * g + 0.1140655078 * b;
  const b_lab = 0.0883024619 * r + 0.2788685167 * g + 0.6328310214 * b;

  const l_ = Math.pow(l_lab, 1/3);
  const a_ = Math.pow(a_lab, 1/3);
  const b_ = Math.pow(b_lab, 1/3);

  const L = 0.2104542553 * l_ + 0.7936177850 * a_ - 0.0040720401 * b_;
  const a = 1.9779984951 * l_ - 2.4285922050 * a_ + 0.4505937099 * b_;
  const b_ok = 0.0259040371 * l_ + 0.7827717662 * a_ - 0.8086757660 * b_;

  // 3. OKLab to OKLCH
  const C = Math.sqrt(a * a + b_ok * b_ok);
  let H = (Math.atan2(b_ok, a) * 180) / Math.PI;
  if (H < 0) H += 360;

  // Format with fixed precision
  return `oklch(${L.toFixed(3)} ${C.toFixed(3)} ${H.toFixed(1)})`;
};

export const hslToHex = (h: number, s: number, l: number): string => {
  h = h / 360;
  s = s / 100;
  l = l / 100;
  
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  const rInt = Math.round(r * 255);
  const gInt = Math.round(g * 255);
  const bInt = Math.round(b * 255);
  
  return `#${((1 << 24) + (rInt << 16) + (gInt << 8) + bInt).toString(16).slice(1).toUpperCase()}`;
};

export const generateShades = (hex: string, count: number) => {
  const { h, s, l } = hexToHslValues(hex);
  const shades = [];
  
  // Define exact fixed percentages based on count
  let percentages: number[] = [];
  
  switch (count) {
    case 2:
      percentages = [40, 80];
      break;
    case 3:
      percentages = [30, 60, 90];
      break;
    case 4:
      percentages = [25, 50, 75, 95];
      break;
    case 5:
      percentages = [18, 40, 65, 80, 97];
      break;
    default:
      percentages = [40, 80]; // fallback to 2 count
  }
  
  percentages.forEach((percentage, index) => {
    // Check if this is a low saturation color (grayscale)
    const isLowSaturation = s < 15;
    
    let newH, newS, newL;
    
    if (isLowSaturation) {
      // For low saturation colors, keep hue and saturation unchanged
      newH = h;
      newS = s;
    } else {
      // Subtle hue shift (1.5 degrees per step) 
      const hueShift = (index + 1) * 1.5;
      newH = (h + hueShift) % 360;
      
      // Subtle saturation reduction (8% per step)
      const saturationReduction = (index + 1) * 0.08;
      newS = Math.max(0, s * (1 - saturationReduction));
    }
    
    // Calculate lightness based on percentage (moving towards black)
    if (percentage === 100) {
      newL = 0; // Pure black
    } else {
      newL = Math.max(0, l * (1 - percentage / 100));
    }
    
    shades.push({
      hex: hslToHex(newH, newS, newL),
      name: `Dark ${index + 1}`,
      variable: `-d-${index + 1}`
    });
  });
  
  return shades;
};

export const generateTints = (hex: string, count: number) => {
  const { h, s, l } = hexToHslValues(hex);
  const tints = [];
  
  // Define exact fixed percentages based on count
  let percentages: number[] = [];
  
  switch (count) {
    case 2:
      percentages = [40, 80];
      break;
    case 3:
      percentages = [30, 60, 90];
      break;
    case 4:
      percentages = [25, 50, 75, 95];
      break;
    case 5:
      percentages = [18, 40, 65, 80, 97];
      break;
    default:
      percentages = [40, 80]; // fallback to 2 count
  }
  
  percentages.forEach((percentage, index) => {
    // Check if this is a low saturation color (grayscale)
    const isLowSaturation = s < 15;
    
    let newH, newS, newL;
    
    if (isLowSaturation) {
      // For low saturation colors, keep hue and saturation unchanged
      newH = h;
      newS = s;
    } else {
      // Subtle hue shift (1.8 degrees per step)
      const hueShift = (index + 1) * 1.8;
      newH = (h + hueShift) % 360;
      
      // Subtle saturation adjustment (slight increase for vibrancy)
      const saturationIncrease = (index + 1) * 0.02;
      newS = Math.min(100, s * (1 + saturationIncrease));
    }
    
    // Calculate lightness based on percentage (moving towards white)
    if (percentage === 100) {
      newL = 100; // Pure white
    } else {
      // Blend towards white based on percentage
      newL = Math.min(100, l + (100 - l) * (percentage / 100));
    }
    
    tints.push({
      hex: hslToHex(newH, newS, newL),
      name: `Light ${index + 1}`,
      variable: `-l-${index + 1}`
    });
  });
  
  return tints;
};

// Add parsing utilities that were in ColorSwatch.tsx
export const parseColorValue = (value: string, format: string): string | null => {
  const cleanValue = value.trim();
  
  if (format === 'hex') {
    const hexMatch = cleanValue.match(/^#?([a-f\d]{6})$/i);
    return hexMatch ? `#${hexMatch[1].toUpperCase()}` : null;
  }
  
  if (format === 'rgb') {
    const rgbMatch = cleanValue.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
    if (rgbMatch) {
      const r = Math.min(255, Math.max(0, parseInt(rgbMatch[1])));
      const g = Math.min(255, Math.max(0, parseInt(rgbMatch[2])));
      const b = Math.min(255, Math.max(0, parseInt(rgbMatch[3])));
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
    }
  }
  
  if (format === 'hsl') {
    const hslMatch = cleanValue.match(/hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/i);
    if (hslMatch) {
      const h = parseInt(hslMatch[1]) / 360;
      const s = parseInt(hslMatch[2]) / 100;
      const l = parseInt(hslMatch[3]) / 100;
      
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      
      let r, g, b;
      if (s === 0) {
        r = g = b = l;
      } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
      }
      
      const rInt = Math.round(r * 255);
      const gInt = Math.round(g * 255);
      const bInt = Math.round(b * 255);
      
      return `#${((1 << 24) + (rInt << 16) + (gInt << 8) + bInt).toString(16).slice(1).toUpperCase()}`;
    }
  }
  
  return null;
};

export const parseTransparencyColorValue = (value: string, format: string): string | null => {
  const cleanValue = value.trim();
  
  if (format === 'hexa') {
    const hexaMatch = cleanValue.match(/^#?([a-f\d]{6})([a-f\d]{2})$/i);
    return hexaMatch ? `#${hexaMatch[1].toUpperCase()}` : null;
  }
  
  if (format === 'rgba') {
    const rgbaMatch = cleanValue.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/i);
    if (rgbaMatch) {
      const r = Math.min(255, Math.max(0, parseInt(rgbaMatch[1])));
      const g = Math.min(255, Math.max(0, parseInt(rgbaMatch[2])));
      const b = Math.min(255, Math.max(0, parseInt(rgbaMatch[3])));
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
    }
  }
  
  if (format === 'hsla') {
    const hslaMatch = cleanValue.match(/hsla\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*,\s*([\d.]+)\s*\)/i);
    if (hslaMatch) {
      const h = parseInt(hslaMatch[1]) / 360;
      const s = parseInt(hslaMatch[2]) / 100;
      const l = parseInt(hslaMatch[3]) / 100;
      
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      
      let r, g, b;
      if (s === 0) {
        r = g = b = l;
      } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
      }
      
      const rInt = Math.round(r * 255);
      const gInt = Math.round(g * 255);
      const bInt = Math.round(b * 255);
      
      return `#${((1 << 24) + (rInt << 16) + (gInt << 8) + bInt).toString(16).slice(1).toUpperCase()}`;
    }
  }
  
  return null;
};

export const generateTransparencies = (hex: string, count: number) => {
  const transparencies = [];
  
  for (let i = 1; i <= count; i++) {
    let opacity;
    
    if (i === 1) {
      opacity = 5;
    } else if (i === 2) {
      opacity = 10;
    } else {
      opacity = (i - 3) * 10 + 20;
    }
    
    transparencies.push({
      hex,
      opacity: opacity,
      name: `${opacity}%`,
      variable: `-${opacity}`
    });
  }
  
  return transparencies;
};

export const getTransparencyColorValue = (hex: string, opacity: number, format: string) => {
  const alpha = opacity / 100;
  switch (format) {
    case 'hex':
    case 'hexa': {
      const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0').toUpperCase();
      return `${hex}${alphaHex}`;
    }
    case 'rgb':
    case 'rgba': {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!result) return 'Invalid';
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    case 'hsl':
    case 'hsla': {
      const { h, s, l } = hexToHslValues(hex);
      return `hsla(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%, ${alpha})`;
    }
    case 'oklch': {
      const oklchBase = hexToOklch(hex);
      if (oklchBase === 'Invalid') return 'Invalid';
      return oklchBase.replace(')', ` / ${alpha})`);
    }
    default:
      return hex;
  }
};

export const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

